'use strict';
/* global window */

const adUtil = require('../../lib/adUtil');
const pricing = require('../../adAuction/pbjsPricing');

const evalTemplate = adUtil.evalTemplate;

module.exports = { generateTargeting, generateTargetingParams, generateNonlinearTargeting };

function _makeUrl(vastParams) {
    let list = [];
    for (let key in vastParams) {
        list.push(key + '=' + vastParams[key]);
    }

    const url = 'https://pubads.g.doubleclick.net/gampad/ads?' + list.join('&');

    return { tag: url };
}

function generateNonlinearTargeting({ gdprApplicable }) {
    // This differs from our normal VAST tag generation in a few ways. We're
    // not concerned with any of our prebid funcctionality at the moment, so we
    // can force this to be direct-only and disable our prebid checks. Our
    // dimensions are hardcoded.
    return generateTargetingParams({
        dimensions: { h: 529, w: 940 },
        directOnly: true,
        encode: true,
        gdprApplicable,
        nonlinear: true,
        skipPrebid: true,
    }).then((params) => (params ? _makeUrl(params) : false));
}

function generateTargeting(opts) {
    return generateTargetingParams({
        ...opts,
        encode: true,
    }).then((params) => (params ? _makeUrl(params) : params));
}

function generateTargetingParams({
    adId,
    videoPlatformId,
    slotModel,
    dimensions,
    maxDuration,
    gdprApplicable,
    encode,
    directOnly,
    nonlinear,
    permutive,
}) {
    if (!window.phxPageTarget) {
        return Promise.resolve(false);
    }

    const config = window.phxPageTarget.config.global.config;
    const baseModel = window.phxPageTarget.config.global.baseModel;
    const { hostname, path, adSlotKey } = baseModel;

    if (!dimensions) {
        // The default size for our preroll targeting is 940x529
        dimensions = { h: 529, w: 940 };
    }

    return new Promise((resolve, reject) => {
        // Leveraging phxAdAuctionQueue to supply key/value data for pageModel
        // ("cs", "pageOfDay", in particular).
        window.phxAdAuctionQueue.push(function () {
            const pageModel = adUtil.makePageModel(baseModel, this.keyValues, gdprApplicable);

            const adUnitPathParts = config.adUnitPathTemplates
                .map((template) => evalTemplate(template, pageModel))
                .filter((part) => !!part)
                .map((part) => (Array.isArray(part) ? part.join('/') : encodeURIComponent(part)));

            const adUnitPath = ['', config.networkCode].concat(adUnitPathParts).join('/');

            const currentLocation = window.location.protocol + '//' + hostname + path;
            let pageTargeting = adUtil.makePageTargeting(
                config.customTargetingPageTemplates,
                pageModel,
                config
            );
            let placementTargetingParams = {};
            if (slotModel) {
                let adzoneindex = slotModel.zone + '_' + slotModel.index;
                placementTargetingParams = {
                    adindex: slotModel.index + '',
                    adzone: slotModel.zone,
                    adzoneindex,
                    siteadzoneindex: hostname + '_' + adzoneindex,
                };
            } else if (nonlinear) {
                placementTargetingParams = {
                    adindex: 0,
                    adzone: 'nonlinear',
                    adzoneindex: 'nonlinear_0',
                    siteadzoneindex: hostname + '_nonlinear_0',
                };
            }
            placementTargetingParams.direct = directOnly ? '1' : '0';
            placementTargetingParams.deal = '0';
            placementTargetingParams.permutive = permutive;
            if (window.pbjs && adId) {
                // If we have a PrebidJS object, check for a 'hb_deal' target applied
                // for this ad unit. If so, pass through this target.
                //
                // Note that pbjs.getAdserverTargetingForAdUnitCode always returns an object.
                let pbTargeting = window.pbjs.getAdserverTargetingForAdUnitCode(adId);
                if (pbTargeting['hb_deal']) {
                    placementTargetingParams.deal = '1';
                }

                // Apply a custom 'hb_pbd' (pre-bid price bucket dollar) target
                let pbd = 0;
                if (pbTargeting['hb_pb']) {
                    pbd = pricing.getCpmDollarBucket(parseFloat(pbTargeting['hb_pb']));
                }
                placementTargetingParams.hb_pbd = pbd.toString();
            }
            let vastParams = {
                description_url: currentLocation,
                sz: dimensions.w + 'x' + dimensions.h,
                impl: 's',
                gdfp_req: 1,
                env: 'vp',
                output: 'vmap',
                ad_rule: 1,
                unviewed_position_start: 1,
                url: currentLocation,
                vid: videoPlatformId,
                cmsid: adSlotKey,
                vpmute: 1,
                plcmt: 2,
            };
            if (nonlinear) {
                vastParams.vad_type = 'nonlinear';
            }
            // Use window.google_global_correlator if it exists; otherwise, a 16-digit
            // number (not a timestamp, per ADS-2700).
            vastParams.correlator = window.google_global_correlator || Math.floor(Math.random() * 10 ** 16);
            vastParams.iu = adUnitPath;
            vastParams.cust_params = adUtil.vastCustomParams(placementTargetingParams, pageTargeting, encode);
            // If we have a defined max duration, set it; note that it comes as
            // seconds from the site config but the VAST tag wants it in ms.
            if (maxDuration) {
                vastParams.max_ad_duration = maxDuration * 1000;
            }
            if (slotModel) {
                if (['preroll', 'midroll', 'postroll'].includes(slotModel.zone)) {
                    vastParams.vpos = slotModel.zone;
                }
            }

            if (gdprApplicable) {
                const gdpr = window.GdprStatus;
                gdpr.isConsentGivenForPurposes(gdpr.PURPOSE_IDS.advertising).then((isConsentGiven) => {
                    // No personalized advertising if GDPR is applicable
                    if (!isConsentGiven) {
                        vastParams.npa = 1;
                    }

                    resolve(vastParams);
                });
            } else {
                resolve(vastParams);
            }
        });
    });
}
